import axios from 'axios'
import {
  ParamsTicketsRequest,
  ReportTicketsResponse,
  TicketActionResponse,
  TicketListResponse,
} from './_models'
import { ROLE, UserModel } from '../../auth'
import { getUserRolesByHierarchy } from '../../auth/PrivateRoute'
import { API_URL } from '../../../../constants'

export const GET_LIST_TICKETS = `${API_URL}/api/payment/list`
export const GET_EMPLOYEE_LIST_TICKETS = `${API_URL}/api/internal/payment/list`
export const INIT_TICKET = `${API_URL}/api/internal/payment/init`
export const TICKET_APPEND_MERCHANT = `${API_URL}/api/internal/payment/append-merchant/`
export const UPLOAD_TICKET_FILE = `${API_URL}/api/payment/bill/`
export const FINISH_TOKEN = `${API_URL}/api/payment/complete/__token__`
export const CANCEL_TOKEN = `${API_URL}/api/payment/decline/__token__`
export const FINISH_EMPLOYEE_TOKEN = `${API_URL}/api/internal/payment/complete/__token__`
export const CANCEL_EMPLOYEE_TOKEN = `${API_URL}/api/internal/payment/decline/__token__`
export const CONFIRM_TICKET_BY_TOKEN = `${API_URL}/api/internal/payment/confirm/`
export const REPORT_TICKETS = `${API_URL}/api/reports/tickets/__type__/summary`
export const REPORT_EXPORT = `${API_URL}/api/reports/tickets/__type__/full`
export const FINISH_TRANSACTIONS_LIST = `${API_URL}/api/payment/transaction/list`
export const SET_FAKE_TICKET_BY_TOKEN = `${API_URL}/api/payment/toggle-fake/__token__`
export const RESEND_CALLBACK = `${API_URL}/api/internal/callback/resend`

export interface ResendCallbackResponse {
  success: boolean
  errors?: Record<string, string>
}

// Manually resend the merchant callback (КБ) for a ticket using its current state.
// Works for both payment and payout tickets (resolved by token on the backend).
export function resendCallbackAPI(token: string) {
  return axios.post<ResendCallbackResponse>(RESEND_CALLBACK, { token })
}

export function getListTickets(params: Partial<ParamsTicketsRequest>, currentUser?: UserModel) {
  if (!params.perPage) {
    params.perPage = 99
  }

  if (!params.page) {
    params.page = 1
  }

  const allRolesCurrentUser = getUserRolesByHierarchy(currentUser)
  // The staff list: it takes withLiveConcierge and answers the concierge flag, which the merchant
  // list does not — every role of the concierge window reads it.
  const url = (
    allRolesCurrentUser.includes(ROLE.ROLE_EMPLOYEE) ||
    allRolesCurrentUser.includes(ROLE.ROLE_MERCHANT_MANAGER) ||
    allRolesCurrentUser.includes(ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR)
  )
    ? GET_EMPLOYEE_LIST_TICKETS
    : GET_LIST_TICKETS

  return axios.get<TicketListResponse>(url, {
    params: params,
  })
}

export function initTicketAPI(params: {
  requisiteId?: string
  merchantToken: string
  amount: string
}) {
  return axios.post(INIT_TICKET, { ...params, amount: parseFloat(params.amount) })
}

export function getListTransactions(
  params: Partial<ParamsTicketsRequest>,
  exportCsv: boolean = false
) {
  if (!params.perPage) {
    params.perPage = 99
  }

  if (!params.page) {
    params.page = 1
  }

  const url = FINISH_TRANSACTIONS_LIST

  return axios.get(url, exportCsv ? { params: params, responseType: 'blob' } : { params: params })
}

export function ticketAppendMerchantGet(token: string, merchantToken: string) {
  return axios.get(`${TICKET_APPEND_MERCHANT}${token}`, {
    params: {
      merchantToken,
    },
  })
}

export function ticketFileGet(url: string) {
  return axios.get(url, { responseType: 'blob' })
}

export function uploadTicketFilePost(formData: FormData, token: string) {
  return axios.post(`${UPLOAD_TICKET_FILE}${token}`, formData)
}

export function finishTicketAPI(
  token: string,
  externalUser?: string,
  amount?: string,
  currentUser?: UserModel
) {
  const allRolesCurrentUser = getUserRolesByHierarchy(currentUser)
  const url = (allRolesCurrentUser.includes(ROLE.ROLE_EMPLOYEE) || allRolesCurrentUser.includes(ROLE.ROLE_MERCHANT_MANAGER))
    ? FINISH_EMPLOYEE_TOKEN
    : FINISH_TOKEN

  const data = {
    ...(externalUser !== undefined ? { externalUserId: externalUser } : {}),
    ...(amount !== undefined ? { amount } : {}),
  }

  const finalUrl = url.replace('__token__', token)

  return Object.keys(data).length
    ? axios.post<TicketActionResponse>(finalUrl, data)
    : axios.post<TicketActionResponse>(finalUrl)
}

export function internalFinishTicketAPI(
  token: string,
  externalUser: string,
  amount?: string,
  currentUser?: UserModel
) {
  const url =
    currentUser?.roles.includes(ROLE.ROLE_SENIOR_OPERATOR) ||
    currentUser?.roles.includes(ROLE.ROLE_OPERATOR)
      ? FINISH_TOKEN
      : FINISH_EMPLOYEE_TOKEN
  
  const data = {
    ...(externalUser ? { externalUserId: externalUser } : {}),
    ...(amount ? { amount } : {}),
  }

  const finalUrl = url.replace('__token__', token)

  return Object.keys(data).length
    ? axios.post<TicketActionResponse>(finalUrl, data)
    : axios.post<TicketActionResponse>(finalUrl)
}

export function cancelTicketAPI(token: string, currentUser?: UserModel) {
  const allRolesCurrentUser = getUserRolesByHierarchy(currentUser)
  const url = (allRolesCurrentUser.includes(ROLE.ROLE_EMPLOYEE) || allRolesCurrentUser.includes(ROLE.ROLE_MERCHANT_MANAGER))
    ? CANCEL_EMPLOYEE_TOKEN
    : CANCEL_TOKEN

  return axios.get<TicketActionResponse>(url.replace('__token__', token))
}

export function internalCancelTicketAPI(token: string, currentUser?: UserModel) {
  const url =
    currentUser?.roles.includes(ROLE.ROLE_SENIOR_OPERATOR) ||
    currentUser?.roles.includes(ROLE.ROLE_OPERATOR)
      ? CANCEL_TOKEN
      : CANCEL_EMPLOYEE_TOKEN
  return axios.get<TicketActionResponse>(url.replace('__token__', token))
}

export function confirmTicketAPI(token: string) {
  return axios.get<TicketActionResponse>(`${CONFIRM_TICKET_BY_TOKEN}${token}`)
}

export function reportTicketsAPI(filterData: any, exportXslx?: boolean) {
  const url = exportXslx ? REPORT_EXPORT : REPORT_TICKETS
  const copyObject = { ...filterData }

  delete copyObject.ticketType

  return axios.post<Partial<ReportTicketsResponse>>(
    url.replace('__type__', filterData.ticketType + ''),
    copyObject,
    exportXslx ? { responseType: 'blob' } : {}
  )
}

export function setFakeTicketAPI(token: string) {
  const url = SET_FAKE_TICKET_BY_TOKEN
  return axios.post<TicketActionResponse>(url.replace('__token__', token))
}
